import * as vp from 'vopay';
import { FunctionComponent, useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { usePermissions } from '@/providers/Permissions.provider';
import useFileDownload from '@/hooks/useFiledDownload';
import ScheduledTransactionOverview from './ScheduledTransactionOverview';
import useCancelAccountTransferSchedule from '@/hooks/useApi/useCancelAccountTransferSchedule';
import useFetchAccountTransferSchedules from '@/hooks/useApi/useFetchAccountTransferSchedules';

import TransactionsHistory from './Transactions/TransactionHistory';
import styles from '../ScheduledTransactions.module.scss';

import CancelTransactionDialogue from '../../Dialogue/CancelTransaction/CancelTransactionDialogue';
import CancelTransactionContext, {
  CancelTransactionCtx,
} from '../../Dialogue/CancelTransaction/CancelTransactionContext';
import { CancelTransactionDialogueHandler, DialogueHandlerProps } from '../../Dialogue/types';
import { useTranslation } from 'react-i18next';
import { UtilButton } from 'UI';

const { Box, Title, Button, DropDown } = vp;

type FC = FunctionComponent;

type URLParams = {
  TransferID: string;
};

const ScheduledTransactionDetailsContainer: FC = () => {
  const { TransferID } = useParams<URLParams>();
  const { printFile, downloadPdfTable } = useFileDownload();
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [hasNewStatus, setNewStatus] = useState(true);

  const { permissions } = usePermissions();
  const [showCancelTransactionConfirmAlert, setShowCancelTransactionConfirmAlert] = useState(false);
  const [showCancelTransactionErrorAlert, setShowCancelTransactionErrorAlert] = useState(false);
  const [sendCancelTransactionError, setSendCancelTransactionError] = useState('');
  const { cancelAccountTransferSchedule } = useCancelAccountTransferSchedule();
  const [scheduledTransactionsResponse, fetchScheduledTransactions] = useFetchAccountTransferSchedules({});
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.Schedule.Details' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const history = useHistory();

  const CancelTransaction: CancelTransactionCtx = {
    showCancelTransactionConfirmAlert,
    showCancelTransactionErrorAlert,
    sendCancelTransactionError,
  };

  const handlers = {
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelTransactionDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelTransactionDialogueHandler = {
        alert: {
          confirm: {
            default: async () => {
              setShowCancelTransactionConfirmAlert(false);
              try {
                await cancelAccountTransferSchedule({ ScheduledTransferID: TransferID });
                setNewStatus(true);
              } catch (error) {
                setShowCancelTransactionErrorAlert(true);
                setSendCancelTransactionError(error instanceof Error ? error.message : '');
              }
            },
          },
          fail: {
            default: () => {
              setShowCancelTransactionConfirmAlert(false);
            },
          },
        },
        error: {
          confirm: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowCancelTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  useEffect(() => {
    if (hasNewStatus) {
      fetchScheduledTransactions({
        ScheduledTransferID: TransferID,
      });
    }
  }, [TransferID, hasNewStatus]);

  useEffect(() => {
    setNewStatus(false);
  }, [scheduledTransactionsResponse]);

  useEffect(() => {
    if (isExportingPDF) {
      downloadPdfTable('scheduledTransactions', 'scheduled-transactions');
    }
    if (isPrinting) {
      printFile('scheduledTransactions');
    }
    setIsExportingPDF(false);
    setIsPrinting(false);
  }, [isExportingPDF, isPrinting]);

  return (
    <CancelTransactionContext.Provider value={CancelTransaction}>
      <div id="scheduledTransactions">
        <Box flx="col" gapSize="16px">
          <UtilButton
            iconLeft="chevron_left"
            size="large"
            onClick={() => {
              if (history.length > 1) {
                history.goBack();
              } else {
                history.replace('/scheduled-transactions');
              }
            }}
          >
            {history.length > 1 ? tGlobal('Buttons.back') : t('title')}
          </UtilButton>
          <Box width="100%" flx="row" jc="sb">
            <Box mb="md">
              <Title title={t('title')} />
            </Box>
            <div className="hiddenFormsPrint">
              <Box flx="row" gap="sm">
                {Object.values(scheduledTransactionsResponse.ScheduledClientAccountTransfers)[0]?.Status ===
                  'in progress' &&
                  permissions.TransactionCancellation && (
                    <Button
                      id="cancel-scheduled-transactions"
                      label={t('cancelTransactionButton')}
                      color="secondary"
                      onClick={() => {
                        setShowCancelTransactionConfirmAlert(true);
                      }}
                    />
                  )}
                <div className={styles.dropdownContainer}>
                  <Button
                    id="export-transactions"
                    color="none"
                    label={tGlobal('ExportDropdown.label')}
                    height="42px"
                    textSize={1}
                    background="transparent"
                    noShadow
                    icon="arrowDown"
                    iconLocation="right"
                    onClick={() => {
                      setIsMenuVisible(true);
                    }}
                  />
                  {isMenuVisible && (
                    <DropDown isMenuVisible={isMenuVisible} onClick={setIsMenuVisible}>
                      <Button
                        id="export-PDF-btn"
                        label={tGlobal('ExportDropdown.Options.pdf')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsExportingPDF(true);
                        }}
                      />
                      <Button
                        id="export-Print-btn"
                        label={tGlobal('ExportDropdown.Options.print')}
                        background="transparent"
                        textSize={1}
                        width="100%"
                        noShadow
                        onClick={() => {
                          setIsPrinting(true);
                        }}
                      />
                    </DropDown>
                  )}
                </div>
              </Box>
            </div>
          </Box>

          <ScheduledTransactionOverview transactions={scheduledTransactionsResponse.ScheduledClientAccountTransfers} />

          <TransactionsHistory
            upcomingTransfers={scheduledTransactionsResponse.ScheduledClientAccountTransfers.UpcomingTransfers}
            completedTransfers={scheduledTransactionsResponse.ScheduledClientAccountTransfers.CompletedTransfers}
            transactions={scheduledTransactionsResponse.ScheduledClientAccountTransfers}
          />
        </Box>
      </div>
      <CancelTransactionDialogue handleDialogue={handlers.handleCancelTransactionDialogue} />
    </CancelTransactionContext.Provider>
  );
};

export default ScheduledTransactionDetailsContainer;
